import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer'
import "./BedCounts.css"
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from '../../../redux/actions/profile/profile'
import { useTranslation } from 'react-i18next'
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer'
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer'
import { ProfileAttributesState } from '../../../redux/reducers/getProfile/profileAttributes/getAttributesReducer'
import { ProfileAttributes } from '../../../types/profile/ProfileAttributes'
import { initialBedCountsData } from '../../../types/profile/ProfileAttributes'
import updateProperties from '../../../utils/DataModifier/DataModifier'
import { FormErrorProvider, useFormErrorContext } from '../../../utils/context/FormErrorContext'
import { useNavigation } from '../../../utils/context/NavigationContext'
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages'
import { ControlledAccordion, useAccordionProvider } from '@szhsin/react-accordion'
import AccordionItem, { DefaultAccordionProviderOptions } from '../../../components/common/AccordionItem/AccordionItem'
import { commaSeparate } from '../../../utils/formatter'
import BedCountsTable from './BedCountsTable'
import { getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from '../../../utils/previousIncomeAttributes'
import { GET_PREVIOUS_PROFILE_ATTRIBUTES } from '../../../redux/actionTypes/actionsTypes'
import { ReadOnlyProvider } from '../../../utils/ReadOnlyContext'
import { selectPreviousProfileAttributes, selectProfileGetAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths'
import { getCurrentCampaignYear } from '../../../utils/previousIncomeAttributes'

export interface BedCountsProps {
    readonly?: boolean;
    profileAttributes?: Record<string, number | null>;
}

export default function BedCounts(props: BedCountsProps): JSX.Element {
    const { t } = useTranslation("ltc_profile_attribute")
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
    const previousProfileAttributes: ProfileAttributesState = useSelector(selectPreviousProfileAttributes)
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const dispatch = useDispatch()
    const saveData: SaveDataState = useSelector(selectSaveData)
    const horizontalHeaders = ["classificationHeader", "AAA", "AA", "A", "B", "C", "D", "hTotalLicensedBedsHeader"]
    const verticalHeaders = ["licensedBedTypeHeader", "basicBedsHeader", "semiPrivateBedsHeader", "privateBedsHeader", "otherHeader", "vTotalLicensedBedsHeader"]
    const basicCellNames = ["basicBedAaa", "basicBedAa", "basicBedA", "basicBedB", "basicBedC", "basicBedD"]
    const semiCellNames = ["semiPrivateBedAaa", "semiPrivateBedAa", "semiPrivateBedA", "semiPrivateBedB", "semiPrivateBedC", "semiPrivateBedD"]
    const privateCellNames = ["privateBedAaa", "privateBedAa", "privateBedA", "privateBedB", "privateBedC", "privateBedD"]
    const otherCellNames = ["otherBedAaa", "otherBedAa", "otherBedA", "otherBedB", "otherBedC", "otherBedD"]
    const keys = basicCellNames.concat(semiCellNames).concat(privateCellNames).concat(otherCellNames)
    const [data, setData] = useState<Record<string, number | null>>(() => {
        const initialData: Record<string, number | null> = {}
        keys.forEach((key) => {
            initialData[key] = null
        })
        return initialData
    })
    const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string)
    const { direction } = useNavigation()
    const { errors, clearErrors } = useFormErrorContext()
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
    const currentCampaignYear = getCurrentCampaignYear()

    useEffect(() => {
        if (!props.profileAttributes) {
            dispatch(resetGetProfileAttributes())
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
            const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

            if (campaign) {
                dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, campaign.currentSnapshotId, GET_PREVIOUS_PROFILE_ATTRIBUTES))
            }

        } else {
            setData(props.profileAttributes);
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!props.profileAttributes) {
            const initData = { ...initialBedCountsData }
            if (!profileAttributes.loading && profileAttributes.success && profileAttributes.data) {
                updateProperties(initData, profileAttributes.data)
            }
            setData(initData)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileAttributes.data, profileAttributes.loading])

    const getPreviousYearData = (year: string) => {
        const sessionStoragePreviousSnapshotDataInternal = sessionStorage.getItem("previousSnapshotDataInternal" + year)
        if (sessionStoragePreviousSnapshotDataInternal !== null && sessionStoragePreviousSnapshotDataInternal !== undefined) {
            let previousSnapshotData = { profileAttributes: [] }
            previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotDataInternal)
            return previousSnapshotData?.profileAttributes
        }
        return {}
    }

    const calculateBasicBedTotal = useCallback(() => {
        return (data.basicBedAaa ?? 0)
            + (data.basicBedAa ?? 0)
            + (data.basicBedA ?? 0)
            + (data.basicBedB ?? 0)
            + (data.basicBedC ?? 0)
            + (data.basicBedD ?? 0)
    }, [data.basicBedAaa, data.basicBedAa, data.basicBedA, data.basicBedB, data.basicBedC, data.basicBedD])
    const calculateSemiPrivateBedTotal = useCallback(() => {
        return (data.semiPrivateBedAaa ?? 0)
            + (data.semiPrivateBedAa ?? 0)
            + (data.semiPrivateBedA ?? 0)
            + (data.semiPrivateBedB ?? 0)
            + (data.semiPrivateBedC ?? 0)
            + (data.semiPrivateBedD ?? 0)
    }, [data.semiPrivateBedAaa, data.semiPrivateBedAa, data.semiPrivateBedA, data.semiPrivateBedB, data.semiPrivateBedC, data.semiPrivateBedD])
    const calculatePrivateBedTotal = useCallback(() => {
        return (data.privateBedAaa ?? 0)
            + (data.privateBedAa ?? 0)
            + (data.privateBedA ?? 0)
            + (data.privateBedB ?? 0)
            + (data.privateBedC ?? 0)
            + (data.privateBedD ?? 0)
    }, [data.privateBedAaa, data.privateBedAa, data.privateBedA, data.privateBedB, data.privateBedC, data.privateBedD])
    const calculateOtherBedTotal = useCallback(() => {
        return (data.otherBedAaa ?? 0)
            + (data.otherBedAa ?? 0)
            + (data.otherBedA ?? 0)
            + (data.otherBedB ?? 0)
            + (data.otherBedC ?? 0)
            + (data.otherBedD ?? 0)
    }, [data.otherBedAaa, data.otherBedAa, data.otherBedA, data.otherBedB, data.otherBedC, data.otherBedD])

    useEffect(() => {
        if (saveData.saving && !props.readonly) {
            clearErrors()
            const fulldata = {
                ...profileAttributes.data, ...data,
                basicBedTotalCount: calculateBasicBedTotal(),
                semiPrivateBedTotalCount: calculateSemiPrivateBedTotal(),
                privateBedTotalCount: calculatePrivateBedTotal(),
                otherBedTotalCount: calculateOtherBedTotal()
            }
            const triggerSpectral = isInternal || direction !== "backward"

            dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId, {
                "profile_attributes": fulldata,
                "spectral_section": "bed_counts",
                "trigger_spectral": triggerSpectral,
                "is_internal": isInternal
            }))
        }
    }, [currentSnapshotId, data, dispatch, profileAttributes.data, profileOverview.profile.profile_type_code, saveData.saving, props.readonly, calculateBasicBedTotal, calculateSemiPrivateBedTotal, calculatePrivateBedTotal, calculateOtherBedTotal, direction, clearErrors, isInternal])

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, currentSnapshotId))
        }
    }, [currentSnapshotId, dispatch, profileOverview.profile.profile_type_code, saveResultState.status])

    const calculateAaaBedTotal = () => {
        return (data.basicBedAaa ?? 0)
            + (data.semiPrivateBedAaa ?? 0)
            + (data.privateBedAaa ?? 0)
            + (data.otherBedAaa ?? 0)
    }
    const calculateAaBedTotal = () => {
        return (data.basicBedAa ?? 0)
            + (data.semiPrivateBedAa ?? 0)
            + (data.privateBedAa ?? 0)
            + (data.otherBedAa ?? 0)
    }
    const calculateABedTotal = () => {
        return (data.basicBedA ?? 0)
            + (data.semiPrivateBedA ?? 0)
            + (data.privateBedA ?? 0)
            + (data.otherBedA ?? 0)
    }
    const calculateBBedTotal = () => {
        return (data.basicBedB ?? 0)
            + (data.semiPrivateBedB ?? 0)
            + (data.privateBedB ?? 0)
            + (data.otherBedB ?? 0)
    }
    const calculateCBedTotal = () => {
        return (data.basicBedC ?? 0)
            + (data.semiPrivateBedC ?? 0)
            + (data.privateBedC ?? 0)
            + (data.otherBedC ?? 0)
    }
    const calculateDBedTotal = () => {
        return (data.basicBedD ?? 0)
            + (data.semiPrivateBedD ?? 0)
            + (data.privateBedD ?? 0)
            + (data.otherBedD ?? 0)
    }
    const calculationFunctionArr = [calculateAaaBedTotal, calculateAaBedTotal, calculateABedTotal, calculateBBedTotal, calculateCBedTotal, calculateDBedTotal];

    const calculateTotal = () => {
        return calculateBasicBedTotal() + calculateSemiPrivateBedTotal() + calculatePrivateBedTotal() + calculateOtherBedTotal()
    }

    const updateValue = (value: number | null, key: string) => {
        setData({ ...data, [key]: value })
    }

    return (
        <>
            <h2 className="visually-hidden">{t('bedCounts', { ns: 'navigation' })}</h2>
            <p className="strong">{t('caption')}</p>
            {props.readonly ?
                ((data !== undefined && calculateTotal() > 0) ? <ControlledAccordion providerValue={providerValue}>
                    <AccordionItem header={t("bedCounts", { ns: 'navigation' })}
                        key="bedCounts"
                        itemKey="bedCounts">
                        <div className='summary-grid'>
                            {horizontalHeaders.slice(1, 7).map((header, index) => {
                                if (calculationFunctionArr[index]() > 0) {
                                    return <div className='summary-section-container' key={`bed-count-section-${index}`}><table className='summary-table bed-counts-summary-table'>
                                        <thead>
                                            <tr>
                                                <th>{header}</th>
                                                <th>{commaSeparate(calculationFunctionArr[index]())}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {["basicBed", "semiPrivateBed", "privateBed", "otherBed"].map((type, i) => {
                                                let firstUpper = header
                                                if (header.length > 1) {
                                                    firstUpper = header[0] + header.substring(1).toLowerCase();
                                                }
                                                const key = type + firstUpper;
                                                return <tr key={`bed-count-section-${index}-${i}`}>
                                                    <td>
                                                        {t(verticalHeaders[i + 1])}
                                                    </td>
                                                    <td>
                                                        {(data[key as keyof ProfileAttributes] ? commaSeparate(data[key as keyof ProfileAttributes]?.toString()) : "")}
                                                    </td>
                                                </tr>
                                            })}
                                        </tbody>
                                    </table></div>
                                } else {
                                    return <></>
                                }

                            }
                            )}
                        </div>
                    </AccordionItem>
                </ControlledAccordion> : <></>)
                :
                <div className='partial-width'>
                    <ErrorMessages errors={errors} />
                    {(data !== undefined) &&
                        <>
                            {(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? (
                                <>
                                    <ControlledAccordion providerValue={providerValue}>
                                        <AccordionItem
                                            header={`${getCurrentCampaignYear()} Campaign`}
                                            headingLevel={2}
                                            key="currentCampaign"
                                            itemKey="currentCampaign">
                                            <BedCountsTable
                                                data={data}
                                                updateValue={updateValue}
                                            />
                                        </AccordionItem>
                                    </ControlledAccordion>
                                    {shouldDisplayPreviousYearAttributes() ? <div className="previous-campaign">
                                        <ReadOnlyProvider isInitiallyReadOnly={true}>
                                            <FormErrorProvider>
                                                <ControlledAccordion providerValue={providerValue}>
                                                    <AccordionItem
                                                        header={`${getCurrentCampaignYear() - 1} Campaign`}
                                                        headingLevel={2}
                                                        key="previousCampaign"
                                                        itemKey="previousCampaign">
                                                        <BedCountsTable
                                                            data={isInternal ? getPreviousYearData(String(currentCampaignYear - 1)) as Record<string, number | null> || {} : previousProfileAttributes.data as Record<string, number | null> || {}}
                                                            updateValue={() => { console.log("Operation Unsupported") }}
                                                        />
                                                    </AccordionItem>
                                                </ControlledAccordion>
                                            </FormErrorProvider>
                                        </ReadOnlyProvider>
                                    </div> : <></>}
                                    {(isInternal && shouldDisplayPreviousTwoYearAttributes()) ?
                                        <div className="previous-campaign">
                                            <ReadOnlyProvider isInitiallyReadOnly={true}>
                                                <ControlledAccordion providerValue={providerValue}>
                                                    <AccordionItem
                                                        header={`${getCurrentCampaignYear() - 2} Campaign`}
                                                        headingLevel={2}
                                                        key="previousCampaign2"
                                                        itemKey="previousCampaign2">
                                                        <BedCountsTable
                                                            data={isInternal ? getPreviousYearData(String(currentCampaignYear - 2)) as Record<string, number | null> || {} : previousProfileAttributes.data as Record<string, number | null> || {}}
                                                            updateValue={() => { console.log("Operation Unsupported") }}
                                                        />
                                                    </AccordionItem>
                                                </ControlledAccordion>
                                            </ReadOnlyProvider>
                                        </div> : <></>}
                                </>
                            ) : (
                                <BedCountsTable
                                    data={data}
                                    updateValue={updateValue}
                                />
                            )}
                        </>
                    }
                </div>}
        </>
    )
}
